import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import FAQAccordion from './FAQAccordion';
import Seo from '../Seo';

const cyberHeroImage = '/award-logos/cyber1.png';
const cyberSecondaryImage = '/award-logos/cyber2.png';
const cyberFallbackImage = '/award-logos/cyber1.png';

const capabilities = [
  {
    title: 'Threat Intel & Active Hunting',
    desc: 'Integrate global threat feeds with internal telemetry to proactively identify and neutralize sophisticated adversaries before they penetrate your perimeter.'
  },
  {
    title: 'Zero Trust Architecture',
    desc: 'Eliminate implicit trust by enforcing strict identity verification, least-privilege access, and micro-segmentation across hybrid-cloud environments.'
  },
  {
    title: 'Identity & Access (IAM/IGA)',
    desc: 'Implement robust identity governance including Multi-Factor Authentication (MFA), Single Sign-On (SSO), and lifecycle management for complex workforces.'
  },
  {
    title: 'Data Protection & Encryption',
    desc: 'Secure sensitive assets at rest and in transit through enterprise-wide encryption, tokenization, and Data Loss Prevention (DLP) frameworks.'
  },
  {
    title: 'Cloud Security Posture (CSPM)',
    desc: 'Automate security configuration monitoring across AWS, Azure, and GCP to prevent misconfigurations and ensure continuous compliance with global standards.'
  },
  {
    title: 'Resilient Incident Response',
    desc: 'Deploy a 24/7 CSIRT (Computer Security Incident Response Team) focused on rapid containment, forensic analysis, and evidence-based recovery.'
  }
];



const useCases = [
  { title: 'Phishing & Ransomware Defense', desc: 'Implementing multi-layered email security and immutable backups to prevent and recover from encryption attacks.' },
  { title: 'Secure Remote Workforce', desc: 'Deploying SASE (Secure Access Service Edge) to protect distributed employees without traditional VPN bottlenecks.' },
  { title: 'Third-Party Risk Management', desc: 'Evaluating and monitoring the security posture of vendors and supply-chain partners to prevent peripheral breaches.' },
  { title: 'Critical Infrastructure (OT) Security', desc: 'Hardening industrial control systems (ICS) and SCADA networks against state-sponsored or targeted cyber threats.' }
];

const serviceModules = [
  {
    title: 'Governance, Risk & Compliance (GRC)',
    points: ['ISO 27001/NIST gap analysis', 'Policy & Procedure development', 'Audit management services']
  },
  {
    title: 'Security Operations (SecOps)',
    points: ['24/7 SIEM/SOAR monitoring', 'Automated threat orchestration', 'Endpoint Detection & Response (EDR)']
  },
  {
    title: 'Application & Cloud Security',
    points: ['Secure SDLC (DevSecOps)', 'Cloud landing zone hardening', 'API security & testing']
  },
  {
    title: 'Identity & Access Governance',
    points: ['Privileged Access Management (PAM)', 'Azure AD/Okta implementation', 'IGA lifecycle automation']
  }
];

const techStack = [
  { title: 'SIEM & SOAR', items: ['Splunk', 'Microsoft Sentinel', 'Palo Alto Cortex', 'LogRhythm'] },
  { title: 'Identity & Access', items: ['Okta', 'Azure AD', 'CyberArk (PAM)', 'Ping Identity'] },
  { title: 'Endpoint & Network', items: ['CrowdStrike', 'SentinelOne', 'Palo Alto Networks', 'Fortinet'] },
  { title: 'Cloud & Data', items: ['Wiz', 'Orca Security', 'Commvault', 'Varonis'] }
];

const industries = ['Banking & Finance', 'Insurance', 'Healthcare', 'Critical Infrastructure', 'Telecom', 'Government', 'Retail'];

const executionSteps = [
  { phase: 'Assessment & Gap Analysis', desc: 'Perform a deep-dive audit against NIST or ISO frameworks to identify critical vulnerabilities and compliance gaps.' },
  { phase: 'Strategy & Architecture', desc: 'Develop a security roadmap and Zero-Trust architecture that aligns with business objectives and risk appetite.' },
  { phase: 'Control Implementation', desc: 'Deploy integrated security toolsets across identity, endpoint, network, and cloud layers with minimal operational friction.' },
  { phase: 'Policy & Governance', desc: 'Establish robust security policies, incident response plans, and employee awareness programs to ensure structural resilience.' },
  { phase: 'Offensive Security Testing', desc: 'Conduct rigorous penetration testing and Red-Teaming to validate the effectiveness of implemented controls.' },
  { phase: 'Continuous Managed Defense', desc: 'Transition to 24/7 monitoring and proactive threat hunting to ensure the enterprise remains resilient as threats evolve.' }
];

const faqs = [
  {
    q: 'How does Libsys approach the "Zero Trust" security model for enterprises?',
    a: 'Zero Trust is not a single product, but a strategic architectural shift. We implement it through three core principles: Verify Explicitly (always authenticate and authorize), Use Least Privilege Access (limit user movement), and Assume Breach (micro-segment networks to contain impact). Our methodology starts by identifying your most critical "protect surface"—the users, devices, and data that matter most—and then building granular security perimeters around them using modern IAM and micro-segmentation toolsets.'
  },
  {
    q: 'How do you handle global compliance requirements like GDPR, HIPAA, and SOC2?',
    a: 'We view compliance as a byproduct of good security. Our GRC (Governance, Risk, and Compliance) team maps your technical controls to multiple global frameworks simultaneously. We use automated compliance-monitoring tools to provide real-time visibility into your audit status. This reduces the manual "evidence collection" burden on your IT team and ensures that you aren\'t just compliant once a year, but rather maintain a continuous state of audit-readiness.'
  },
  {
    q: 'What is your methodology for Ransomware Prevention and Recovery?',
    a: 'Our strategy is multi-layered. For prevention, we focus on EDR (Endpoint Detection and Response) and email security to stop initial entry. For containment, we use micro-segmentation to prevent lateral movement. Most importantly, for recovery, we implement "Immutable Backup" architectures—backups that cannot be deleted or modified even by an administrative user. This ensures that even if an attack succeeds in encrypting your production data, we have a guaranteed clean copy to restore from, eliminating the need to pay ransoms.'
  },
  {
    q: 'How do you differentiate between traditional monitoring and proactive "Threat Hunting"?',
    a: 'Traditional monitoring is reactive; it waits for a known signature to trigger an alert. Threat Hunting is proactive; our analysts assume an adversary is already in the network and use hypothesis-driven searches to find subtle indicators of compromise (IoC) that automated tools might miss. We use advanced behavioral analytics and global threat intelligence to identify unusual patterns in user behavior or network traffic, allowing us to neutralize "Zero-Day" threats before they cause damage.'
  },
  {
    q: 'Can you help us secure our cloud-native applications and DevOps pipelines?',
    a: 'Yes, we specialize in "DevSecOps"—integrating security into the developer workflow. We implement automated security gates in your CI/CD pipelines that scan for vulnerabilities in your code, misconfigurations in your Infrastructure-as-Code (IaC), and hardcoded secrets. We also utilize Cloud Security Posture Management (CSPM) to monitor your AWS, Azure, or GCP environments for drifting security configurations, ensuring your cloud remains as secure as your on-premise data centers.'
  },
  {
    q: 'How does Libsys support organizations during an active security incident?',
    a: 'We provide a tiered Incident Response (IR) service. For retained clients, we offer guaranteed response times with dedicated CSIRT leads. Our process follows the SANS/NIST lifecycle: Preparation, Identification, Containment, Eradication, Recovery, and Lessons Learned. We don\'t just "clean up the mess"—we perform deep forensic analysis to understand how the attacker got in and then help you harden your environment so the same vulnerability can never be exploited again.'
  }
];

const ServiceCyberSecurity = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white min-h-screen pt-8 [&_.text-h1]:text-h2 [&_.text-h2]:text-h3 [&_.text-body]:text-small [&_.text-small]:text-subtext">
      <Seo
        title="Cybersecurity Services"
        description="Libsys cybersecurity services — threat intelligence, network security, compliance controls, SOC operations, and enterprise-grade security posture improvement."
        path="/services/cybersecurity"
      />
      {/* ══ HERO SECTION ══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 p-6 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-5"
          >
            <span className="inline-flex items-center gap-2 text-strategic-tagline mb-5">
              Cyber Resilience & Defense
            </span>
            <h1 className="text-h1 text-primary mb-5">
              Secure the Enterprise with Zero-Trust Resilience
            </h1>
            <p className="text-slate-600 text-body leading-[1.7] font-medium mb-6 max-w-2xl">
              In an era of sophisticated digital threats, traditional perimeter security is no longer sufficient. Libsys delivers advanced cyber defense frameworks that protect your most critical assets through proactive threat hunting, rigorous identity governance, and zero-trust engineering. We bridge the gap between technical risk and business continuity, ensuring your organization remains resilient, compliant, and audit-ready across hybrid-cloud ecosystems. By integrating automated detection and response (EDR/XDR) with immutable data layers, we ensure that your enterprise stays ahead of adversaries while maintaining full sovereign data control.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 mb-8">
              {[
                'Zero-Trust Architecture & Strategy',
                '24/7 Managed Threat Hunting',
                'Continuous Compliance (GDPR/SOC2)',
                'Advanced Data Loss Prevention',
                'Identity Lifecycle Governance',
                'Resilient Incident Response & Forensic'
              ].map((item) => (
                <li key={item} className="text-slate-700 text-subtext font-bold leading-relaxed flex items-start gap-2">
                  <span className="w-2 h-2 rounded-full bg-secondary mt-[7px] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4">
              <a
                href="tel:+16307991556"
                className="inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-[#254b85] text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-blue-900/10 active:scale-95 group"
              >
                Request Security Assessment
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 lg:-mt-12"
          >
            <div className="rounded-[2.5rem] overflow-hidden border border-slate-200 shadow-[0_30px_70px_-15px_rgba(35,96,134,0.2)] bg-slate-100 relative group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
              <img
                src={cyberHeroImage}
                alt="Cyber Defense Simulation"
                className="w-full h-[400px] lg:h-[600px] object-cover group-hover:scale-105 transition-transform duration-1000"
                onError={(e) => { e.currentTarget.src = cyberFallbackImage; }}
              />
            </div>
          </motion.div>
        </div>
        </div>
      </section>

      {/* ══ CORE CAPABILITIES ════════════════════════════════════════════════ */}
      <section className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-14 text-center">
            <span className="text-strategic-tagline block mb-2">Technical Depth</span>
            <h2 className="text-h2 text-primary">Advanced Enterprise Security Stack</h2>
            <p className="text-slate-600 text-body max-w-3xl mx-auto mt-4 leading-relaxed">
              Our security practice combines high-fidelity engineering with strategic risk management, ensuring that controls perform reliably in high-stakes enterprise environments.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item) => (
              <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-[22px] font-black text-primary mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-600 text-subtext leading-relaxed font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SERVICE PORTFOLIO ════════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="rounded-[2.5rem] border border-slate-200 bg-slate-50/70 overflow-hidden shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-8 p-8 lg:p-12">
              <span className="text-strategic-tagline block mb-3">Service Portfolio</span>
              <h2 className="text-h2 text-primary mb-6">Strategic Security Modules</h2>
              <p className="text-slate-600 text-body mb-8 max-w-3xl">
                We provide end-to-end security services with clear roadmap ownership so programs can scale from initial posture assessment to unified enterprise protection.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {serviceModules.map((module) => (
                  <div key={module.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h3 className="text-body font-black text-primary mb-4 flex items-center gap-2">
                       {module.title}
                    </h3>
                    <ul className="space-y-3">
                      {module.points.map((point) => (
                        <li key={point} className="text-slate-600 text-subtext font-semibold leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-100 relative overflow-hidden">
               <img src={cyberSecondaryImage} alt="Security Delivery Pipeline" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.src = cyberFallbackImage; }} />
               <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent flex flex-col justify-end p-8">
                  <p className="text-white text-lg font-black leading-tight italic">"Bridging the gap between technical vulnerability and executive risk confidence."</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ DELIVERY PHASES ══════════════════════════════════════════════════ */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-12 mb-10">
              <span className="text-strategic-tagline block mb-3">Implementation Framework</span>
              <h2 className="text-h2 text-primary mb-6">A Disciplined Path to Cyber Resilience</h2>
              <p className="text-slate-600 text-body max-w-4xl">
                 Success in modern security requires more than just tools; it requires a structured lifecycle that manages risk, architecture, and threat response in lockstep.
              </p>
            </div>
            <div className="lg:col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-10">
                {executionSteps.map((step, idx) => (
                  <div key={step.phase} className="relative pl-12">
                    <div className="absolute left-0 top-0 text-5xl font-black text-slate-200 leading-none">
                      {(idx + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="relative">
                      <h4 className="text-body font-black text-primary mb-2 uppercase tracking-wide">{step.phase}</h4>
                      <p className="text-slate-600 text-subtext leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECH STACK & USE CASES ════════════════════════════════════════════ */}
      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
             <span className="text-strategic-tagline block mb-3 text-center lg:text-left">Technology Ecosystem</span>
             <h2 className="text-h2 text-primary mb-6 text-center lg:text-left">Enterprise-Grade Security Toolsets</h2>
             <p className="text-slate-600 text-body mb-10 max-w-4xl text-center lg:text-left mx-auto lg:mx-0">
                We leverage a specialized toolset that connects proven enterprise environments with the latest proactive innovation to ensure your security controls are resilient and portable. Our selection criteria prioritize integration capability and automated remediation.
             </p>
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {techStack.map(cat => (
                  <div key={cat.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50 hover:bg-white hover:shadow-lg transition-all">
                     <h5 className="font-black text-primary text-tiny uppercase tracking-widest mb-3">{cat.title}</h5>
                     <div className="flex flex-wrap gap-2">
                        {cat.items.map(i => (
                          <span key={i} className="px-2.5 py-1 bg-white border border-slate-200 rounded-md text-tiny font-bold text-slate-600">{i}</span>
                        ))}
                     </div>
                  </div>
                ))}
             </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch pt-12 border-t border-slate-100">
             <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm h-full">
                   <h3 className="text-xl font-black text-primary mb-6">Strategic Use-Cases</h3>
                   <div className="space-y-6">
                      {useCases.map(uc => (
                         <div key={uc.title} className="border-b border-slate-100 pb-6 last:border-b-0 last:pb-0">
                            <h4 className="text-primary font-black text-subtext uppercase tracking-wide mb-2">{uc.title}</h4>
                            <p className="text-slate-600 text-subtext font-medium leading-relaxed">{uc.desc}</p>
                         </div>
                      ))}
                   </div>
                </div>
             </div>

             <div className="space-y-6">
                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                   <h3 className="text-xl font-black text-primary mb-6">Security Maturity Benchmarks</h3>
                   <div className="space-y-4">
                      {[
                        'Zero-Trust network access for all remote workers.',
                        'Automated compliance reporting for global standards.',
                        'Ransomware protection with immutable data layers.',
                        'Integrated Identity lifecycle management (IGA).'
                      ].map(b => (
                         <div key={b} className="flex items-start gap-4">
                            <div className="w-2 h-2 rounded-full bg-secondary mt-2.5 flex-shrink-0" />
                            <p className="text-slate-600 text-body font-medium leading-relaxed">{b}</p>
                         </div>
                      ))}
                   </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8">
                   <h3 className="text-[17px] font-black text-primary mb-4">Target Sectors</h3>
                   <div className="flex flex-wrap gap-2">
                     {industries.map(ind => (
                       <span key={ind} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-subtext font-bold text-slate-600 shadow-sm">{ind}</span>
                     ))}
                   </div>
                </div>
             </div>
          </div>
      </section>

      <FAQAccordion faqs={faqs} />
    </div>
  );
};

export default ServiceCyberSecurity;
